"use client"

import dynamic from "next/dynamic"
import { MouseEvent } from "react"

import { Translate } from "@/components/ui/translate"
import { PDFUrls } from "@/config/consts"
import { isMobile } from "@/lib/is-mobile"

import { useAnalyticsContext } from "@/components/analytics-provider"
import { disclaimerInfoDialog } from "../shared/terms-and-conditions/config/consts"
import { useInfoDialogWithPDF } from "../shared/terms-and-conditions/hooks/use-info-dialog-with-pdf"

const LazyInfoDialogWithPdf = dynamic(
  () =>
    import("@/app/shared/terms-and-conditions/components/info-dialog-with-pdf"),
)

/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/
type PrivacyDisclaimerProps = {
  privacyPdfUrl?: string
  analytics_conversion_pagename?: string
}

/* -------------------------------------------------------------------------------------------------
 * config
 * -----------------------------------------------------------------------------------------------*/

const PrivacyDisclaimer = ({
  privacyPdfUrl = PDFUrls().privacy,
  analytics_conversion_pagename,
}: PrivacyDisclaimerProps) => {
  const [isInfoDialogWithPDFVisible, infoDialogWithPDF] =
    useInfoDialogWithPDF(disclaimerInfoDialog)
  const { sendData } = useAnalyticsContext()

  const handleReadPrivacyInfoPDFClick = (e: MouseEvent<HTMLElement>) => {
    if (analytics_conversion_pagename)
      sendData({
        event: "link_click",
        event_name: "reg_event",
        conversion_pagename: analytics_conversion_pagename,
        position: "informativa_privacy",
        cta_label: "informativa sulla privacy",
      })

    if (isMobile()) {
      // if from mobile, we render the PDF directly inside the infoDialogWithPDF,
      // otherwise the PDF will be open on another tab and downloaded (default <a> tag behavior)
      e.preventDefault()
      infoDialogWithPDF.open()
    }
  }

  return (
    <>
      <div className="text-paragraph-xs text-header-text">
        <Translate
          i18nKey={"SelectRegistrationPage.privacyDisclaimer"}
          values={{
            ReadPrivacyInfoPDF: (chunks) => (
              <a
                className="font-bold underline"
                href={privacyPdfUrl}
                rel="noopener noreferrer"
                target="_blank"
                onClick={handleReadPrivacyInfoPDFClick}
              >
                {chunks}
              </a>
            ),
          }}
        />
      </div>
      {isInfoDialogWithPDFVisible && (
        <LazyInfoDialogWithPdf type="disclaimer" showPdfOnMount={true} />
      )}
    </>
  )
}

export { PrivacyDisclaimer }
